import { ArrowDown } from "lucide-react";
import { NETWORKS, SOCIAL_LINKS, signupUrl } from "@/data/networks";
import { MaskedLines, FadeIn, Reveal } from "@/components/hv/Motion";
import { ArchPhoto } from "@/components/hv/ArchPhoto";
import { Marquee } from "@/components/hv/Marquee";
import { NetworkCard } from "@/components/hv/NetworkCard";
import { Pillars } from "@/components/hv/Pillars";
import { CtaBand } from "@/components/hv/CtaBand";
import { StartHere } from "@/components/hv/StartHere";
import { Testimonials } from "@/components/hv/Testimonials";
import { TourCapture } from "@/components/hv/TourCapture";
import { Seo } from "@/components/hv/Seo";
import { LogoMark } from "@/components/hv/Logo";

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

export default function HubPage() {
  return (
    <div data-testid="hub-page">
      <Seo
        title="HearthVault on Facebook, YouTube, Instagram & LinkedIn"
        description="Follow HearthVault, the family document safe, on Facebook, YouTube, Instagram and LinkedIn — checklists, walkthroughs and advisor notes for the family folder."
        image="/og/hub.jpg"
        imageAlt="HearthVault on social — one family vault, four quiet doorways in"
      />
      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-20 sm:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-10 items-center">
        <div>
          <FadeIn className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">Hearthside · HearthVault on social</FadeIn>
          <MaskedLines
            testId="hub-hero-title"
            className="mt-5 font-serif text-5xl sm:text-6xl lg:text-[88px] leading-[1.02] tracking-tight text-[#1F1A17]"
            lines={["One family vault.", <>Four quiet</>, <em key="e" className="text-[#B4532A]">doorways in.</em>]}
          />
          <FadeIn delay={0.7}>
            <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#685F58]">
              Follow HearthVault wherever you already spend your evenings. Checklists on Facebook, walkthroughs on YouTube, small rituals on Instagram and notes for advisors on LinkedIn — all pointing home to one vault.
            </p>
          </FadeIn>
          <FadeIn delay={0.85} className="mt-9 flex flex-wrap gap-3">
            <a href={signupUrl("hearthside")} data-testid="hub-signup-btn" className="inline-flex items-center gap-2 rounded-full bg-[#B4532A] hover:bg-[#9C421E] h-12 px-6 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">
              <LogoMark className="h-4 w-4" /> Create your vault — free
            </a>
            <a href="#channels" data-testid="hub-channels-link" className="group inline-flex items-center gap-2 rounded-full border border-[#E8DDD3] bg-white h-12 px-6 text-sm font-medium text-[#1F1A17] hover:border-[#1F1A17] transition-colors duration-300">
              Choose a channel <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </FadeIn>
          <FadeIn delay={1} className="mt-10 flex items-center gap-3">
            {NETWORKS.map((n) => (
              <a key={n.key} href={SOCIAL_LINKS[n.key]} target="_blank" rel="noopener noreferrer" aria-label={`Follow on ${n.name}`} data-testid={`hub-hero-follow-${n.key}`}
                className="h-11 w-11 rounded-full border border-[#E8DDD3] bg-white grid place-items-center text-[#1F1A17] transition-all duration-300 hover:-translate-y-1 hover:text-white"
                onMouseEnter={(e) => { e.currentTarget.style.background = n.accent; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
              >
                <n.Icon className="h-4 w-4" />
              </a>
            ))}
          </FadeIn>
        </div>
        <ArchPhoto src="/img/hub.jpg" alt="A grandmother hugging her granddaughter" label="The family folder, kept" />
      </section>

      <Marquee />

      <section id="channels" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="channels-section">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">The channels</div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1F1A17]">Pick the doorway that <em>fits your evening.</em></h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#685F58]">Each channel has its own page, so whichever network you came from, you land somewhere that speaks your language.</p>
        </Reveal>
        <div className="mt-14 grid gap-5 lg:grid-cols-12">
          {NETWORKS.map((n, i) => (
            <Reveal key={n.key} delay={(i % 2) * 0.12} className={SPANS[i]}>
              <NetworkCard n={n} />
            </Reveal>
          ))}
        </div>
      </section>

      <StartHere />
      <Pillars />
      <Testimonials networkKey="hub" />
      <CtaBand source="hearthside" />
      <TourCapture networkKey="hub" />
    </div>
  );
}
